import * as Yup from "yup";

import {
  CreateClosingForOffer,
  CreateClosingForOfferVariables,
} from "_graphql/mutation/__generated__/CreateClosingForOffer";
import {
  useKEKOffer,
  useOffer,
  useOfferUnAcceptedParticipants,
} from "hooks/data/use-offers";
import { useMatch, useNavigate } from "react-location";

import { CloseOfferForm } from "components/forms";
import { LocationGenerics } from "router/location";
import { Modal } from "components";
import _ from "lodash";
import { closeOffer } from "_graphql/visal/mutations/offers";
import { getDomain } from "router/filter";
import toast from "react-hot-toast";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { wrapClick } from "utils";

export const validationSchema = Yup.object().shape({
  requests: Yup.array()
    .of(
      Yup.object().shape({
        withholdingTax: Yup.number()
          .min(0, "Number must be greater than 0")
          .typeError("Should be a number")
          .required("Withholding Tax is required"),
        nicLevy: Yup.number()
          .min(0, "Number must be greater than 0")
          .typeError("Should be a number")
          .required("NIC Levy is required"),
        agreedBrokerage: Yup.number()
          .min(0, "Number must be greater than 0")
          .typeError("Should be a number")
          .required("Agreed Brokerage Tax is required"),
        agreedCommission: Yup.number()
          .min(0, "Number must be greater than 0")
          .typeError("Should be a number")
          .required("Agreed Commission is required"),
      })
    )
    .min(1)
    .required("At least one participant is required"),
  rejected_participants: Yup.array()
    .of(
      Yup.object().shape({
        reason: Yup.string().required("Reason is required"),
      })
    )
    .min(0)
    .required("At least one rejected participant is required"),
});

export default function CloseOfferContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const domain = getDomain();
  const navigate = useNavigate<LocationGenerics>();
  const { params } = useMatch<LocationGenerics>();
  const { offer, loading: fetching } = useKEKOffer({
    offer_id: params.offer || "",
  });

  const { unAcceptedPariticipants, loading: gettingUnacceptedParticipants } =
    useOfferUnAcceptedParticipants({ offerId: params.offer || "" });

  const isFleet = /fleet/i.test(offer?.classofbusiness?.business_name || "");

  const [create, { loading }] = useMutation<
    CreateClosingForOffer,
    CreateClosingForOfferVariables
  >(closeOffer, {
    refetchQueries: ["Offer"],
  });

  const form = useFormik<any>({
    initialValues: {
      requests: [],
      rejected_participants: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (variables) => {
      // console.log("Navigating to create closing", { isFleet });

      const extraCharges = {
        offer_id: params.offer || "",
        reinsurer_data: variables.requests?.map((item: any) => ({
          reinsurer_id: item.reinsurer?.reinsurer_id,
          reinsurer_participant_id: item.offer_participant_id,
          withholding_tax: item.withholdingTax,
          agreed_brokerage_percentage: item.agreedBrokerage,
          agreed_commission: item.agreedCommission,
          nic_levy: item.nicLevy,
        })),
        participant_data: variables.rejected_participants?.map((item: any) => ({
          participant_id: item.offer_published_participant_id,
          reason: item.reason,
        })),
      };
      await create({
        variables: {
          extraCharges:
            domain === "KEK"
              ? (_.omit(extraCharges, ["participant_data"]) as any)
              : extraCharges,
        },
      }).then(({ data }) => {
        // console.log("Navigating to create closing", { isFleet });
        if (
          data?.createClosingForOffer &&
          data?.createClosingForOffer.length > 0
        ) {
          toast.success("Offer Closed Successfully");
          refetch?.();
          form.resetForm();
          if (!isFleet) {
            navigate({ to: "/create-closing" });
          }
        } else {
          toast.error("Could not close Offer");
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    if (
      offer &&
      offer?.offer_participant &&
      offer?.offer_participant?.length > 0
    ) {
      const initialRequests = offer.offer_participant.map((item) => ({
        ...item,
        withholdingTax: 0,
        nicLevy: 0,
        agreedBrokerage: 0,
        agreedCommission: 0,
      }));

      // only set if different
      if (
        JSON.stringify(form.values.requests) !== JSON.stringify(initialRequests)
      ) {
        form.setFieldValue("requests", initialRequests);
      }
    }
  }, [offer]);

  useEffect(() => {
    if (unAcceptedPariticipants?.length > 0) {
      const rejected = unAcceptedPariticipants.map((item) => ({
        ...item,
        reinsurer: item?.reinsurer?.re_company_name,
        reason: "",
      }));

      if (
        JSON.stringify(form.values.rejected_participants) !==
        JSON.stringify(rejected)
      ) {
        form.setFieldValue("rejected_participants", rejected);
      }
    }
  }, [unAcceptedPariticipants]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={fetching || gettingUnacceptedParticipants}
      title="Close Offer"
      size="7xl"
      description="Provide the percentage of the offer to be distributed to each reinsurer."
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Closing Offer..." : "Close Offer"}
          </button>
        </>
      )}
    >
      {/* {JSON.stringify(form.errors, null, 2)} */}
      <CloseOfferForm form={form} />
    </Modal>
  );
}
